"use client";

import React from "react";
import { PhoneCall, Clock, ShieldCheck, ArrowRight, Wrench, CircleCheck as CheckCircle2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const highlights = [{
  text: "Fast 24/7 Dispatch"
}, {
  text: "Licensed & Insured"
}, {
  text: "No Hidden Fees"
}, {
  text: "Jonesboro Local Team"
}];
export default function EmergencyCta() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="emergencycalltoaction" data-nav="light" className="bg-background py-16 sm:py-20 lg:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="relative overflow-hidden rounded-2xl bg-primary text-primary-foreground p-8 sm:p-12 lg:p-16 shadow-xl">
          {/* Subtle background decorative shapes */}
          <div aria-hidden="true" className="pointer-events-none absolute -top-24 -right-24 size-96 rounded-full bg-accent/10 blur-3xl" />
          <div aria-hidden="true" className="pointer-events-none absolute -bottom-24 -left-24 size-96 rounded-full bg-accent/10 blur-3xl" />

          <div className="relative z-10 grid gap-8 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-7 space-y-6">
              <div className="flex flex-wrap items-center gap-2.5">
                <Badge variant="secondary" className="bg-accent text-accent-foreground font-semibold px-3 py-1 text-xs sm:text-sm tracking-normal">
                  <Clock className="size-3.5" />
                  24/7 Emergency Dispatch
                </Badge>
                <span className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-primary-foreground/90 font-medium">
                  <ShieldCheck className="size-4 text-accent" />
                  Jonesboro, GA &amp; Surrounding Areas
                </span>
              </div>

              <div className="space-y-3">
                <h2 className="font-default text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl text-primary-foreground">
                  Plumbing Crisis? We Are Ready to Help Right Now.
                </h2>
                <p className="font-default text-base sm:text-lg text-primary-foreground/90 max-w-2xl leading-relaxed">
                  Burst pipes, overflowing drains, or complete water heater breakdowns require immediate attention.
                  Call Jonesboro Emergency Plumber for quick, reliable, on-demand plumbing repairs.
                </p>
              </div>

              {/* Highlights grid */}
              <div className="grid grid-cols-2 gap-3 pt-2 max-w-lg">
                {highlights.map((item, i) => <div key={item.text} data-index={i} className="flex items-center gap-2 text-sm text-primary-foreground/90 font-medium">
                    <CheckCircle2 className="size-4 text-accent shrink-0" />
                    <span>{item.text}</span>
                  </div>)}
              </div>
            </div>

            <div className="lg:col-span-5 flex flex-col justify-center gap-4 lg:pl-6">
              <div className="rounded-xl bg-card text-card-foreground p-6 sm:p-8 shadow-lg border border-border">
                <div className="text-center space-y-2 mb-6">
                  <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                    Direct Dispatch Hotline
                  </span>
                  <div>
                    <a href="tel:4708479076" className="inline-flex items-center justify-center gap-2 font-default text-2xl sm:text-3xl font-extrabold text-foreground hover:text-accent transition-colors" aria-label="Call emergency plumbing line at (470) 847-9076">
                      <PhoneCall className="size-6 text-accent animate-pulse" />
                      <span>{"(470) 847-9076"}</span>
                    </a>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Live dispatchers available around the clock
                  </p>
                </div>

                <div className="flex flex-col gap-3">
                  <Button asChild size="lg" className="w-full bg-accent text-accent-foreground font-semibold hover:bg-accent/90 shadow-sm">
                    <a href="tel:4708479076">
                      <PhoneCall className="size-4" />
                      Call for Immediate Help
                    </a>
                  </Button>

                  <Button asChild variant="outline" size="lg" className="w-full border-border text-foreground hover:bg-muted font-medium">
                    <Link to="/contact/">
                      <span>Request Service Online</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                </div>

                <div className="mt-4 pt-4 border-t border-border flex items-center justify-center gap-2 text-xs text-muted-foreground text-center">
                  <Wrench className="size-3.5 text-accent shrink-0" />
                  <span>Rapid response vehicles dispatched in Clayton County</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}